import { Injectable } from '@angular/core';
import { EventType, EventMessage, BrowserAuthError, InteractionStatus } from '@azure/msal-browser';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class MsalErrorHandlerService {

  private isRedirecting = false;
  private currentInteractionStatus: InteractionStatus = InteractionStatus.Startup;

  constructor(
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private router: Router
  ) {
    this.initializeErrorHandler();
  }

  private initializeErrorHandler(): void {
    // Stale `msal.requiresLogin` flags from earlier sessions used to re-trigger
    // performLogin() during bootstrap — colliding with MSAL's handleRedirectPromise
    // and producing `interaction_in_progress` errors that left the app stuck.
    // Clear it on startup and never act on it.
    localStorage.removeItem('msal.requiresLogin');

    // Track MSAL interaction state so we don't start a new interactive flow
    // while another (e.g., a redirect handler) is still running.
    this.msalBroadcastService.inProgress$.subscribe((status) => {
      this.currentInteractionStatus = status;
    });

    this.msalService.instance.addEventCallback((event: EventMessage) => {
      if (event.eventType !== EventType.ACQUIRE_TOKEN_FAILURE) {
        return;
      }

      const error = event.error as any;
      console.error('Error ACQUIRE_TOKEN_FAILURE', error);

      if (this.isRedirecting) {
        return;
      }

      const isInvalidGrant = error?.errorCode === 'invalid_grant' ||
        error?.errorCode === 'AADSTS70000' ||
        error?.errorMessage?.includes('AADSTS70041') ||
        error?.errorMessage?.includes('AADSTS70043');

      const isTimeoutError = (error?.errorCode === 'monitor_window_timeout' ||
        error?.errorCode === 'monitor_window_timeout_external' ||
        error instanceof BrowserAuthError) &&
        error?.errorMessage?.includes('timeout');

      // Consent-required failures (AADSTS65001) are also invalid_grant. Don't
      // recover with another loginRedirect — that's what created a redirect
      // loop previously (clearing the cache mid-consent-flow just forces the
      // same consent prompt again). MsalGuard/MsalInterceptor (configured with
      // InteractionType.Redirect) will request consent on the next protected
      // navigation instead.
      const isConsentRequired = error?.errorMessage?.includes('AADSTS65001');
      if (isConsentRequired) {
        console.warn('Consent required for requested scope. Falling through to MsalGuard interactive flow.');
        return;
      }

      if (isInvalidGrant || isTimeoutError) {
        console.log('Token acquisition failed, scheduling login redirect...');
        setTimeout(() => this.tryRecoverLogin(), 100);
      }
    });
  }

  private tryRecoverLogin(): void {
    if (this.isRedirecting) {
      return;
    }
    if (this.currentInteractionStatus !== InteractionStatus.None) {
      // MSAL is mid-flight (handling a redirect, login, or silent acquisition).
      // Starting another interactive flow now would throw `interaction_in_progress`.
      console.warn('Skipping recovery loginRedirect: MSAL interaction status is', this.currentInteractionStatus);
      return;
    }
    this.performLogin();
  }

  private performLogin(): void {
    this.isRedirecting = true;

    // The previous implementation did logout() then loginRedirect() in a subscribe
    // chain. logout() is a top-level redirect, so the browser leaves the page
    // before the chained loginRedirect can run — the user only ever sees the
    // Azure account picker and ends up signed out. loginRedirect with
    // prompt:'login' is enough to force fresh credentials; MSAL replaces stale
    // tokens on the way back without needing an explicit logout.
    const activeAccount = this.msalService.instance.getActiveAccount()
      ?? this.msalService.instance.getAllAccounts()[0];

    this.msalService.loginRedirect({
      scopes: ['user.read', environment.apiScope],
      prompt: 'login',
      account: activeAccount
    });
  }
}
